import { useState, useEffect, useRef, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Mail, Eye, EyeOff, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "@/auth/useAuth";
import { ApiError } from "@/api/client";
import { requestPasswordReset } from "@/api/auth";
import { leggiCondivisi } from "@/pwa/condivisi";
import { ritornoValido } from "@/auth/ritorno";
import { paginaArrivoOperatore } from "@/auth/arrivo";
import { dopoAccesso } from "@/components/preventivo/scritte";

type LoginStep =
  | { kind: "form" }
  | { kind: "magic_sent"; identifier: string }
  | { kind: "not_found"; email: string }
  | { kind: "forgot_password" }
  | { kind: "forgot_sent" };

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/** Card centrata con logo, usata dagli stati secondari (magic-link, password
 * dimenticata, account non trovato) — la schermata principale ("form") ha il
 * suo layout split animato e non passa da qui. */
function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="text-center mb-6">
        <img src="/logo.png" alt="AIEnergia" className="h-10 mx-auto" />
        <div className="text-sm text-muted-foreground mt-2">Accedi al tuo portale</div>
      </div>
      {children}
    </div>
  );
}

export function LoginPage() {
  const { probe, espoLogin, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get("expired") === "1";
  // return-to: impostato da ProtectedRoute quando un deep-link (es. bottone Telegram)
  // arriva senza sessione; dopo il login si torna lì invece che alla dashboard di ruolo
  // con la sessione scaduta a pagina aperta il ritorno viaggia nell'indirizzo (`from`), perché il rimando ricarica tutto
  const returnTo = (useLocation().state as { from?: string } | null)?.from ?? ritornoValido(searchParams.get("from"));

  // arrivando da una condivisione di Android (`?condivisi=1`) i file aspettano sul telefono: si dice quanti
  const [inAttesa, setInAttesa] = useState(0);
  useEffect(() => {
    if (!returnTo?.includes("condivisi=1")) return;
    let viva = true;
    leggiCondivisi().then((c) => { if (viva) setInAttesa(c?.files.length ?? 0); }).catch(() => {});
    return () => { viva = false; };
  }, [returnTo]);

  const [step, setStep] = useState<LoginStep>({ kind: "form" });
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [forgotIdentifier, setForgotIdentifier] = useState("");

  // Google Sign-In
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    // Load GSI script
    if (document.getElementById("gsi-script")) {
      if (window.google?.accounts?.id) {
        setGoogleReady(true);
      }
      return;
    }

    const script = document.createElement("script");
    script.id = "gsi-script";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = () => setGoogleReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!googleReady || !GOOGLE_CLIENT_ID || !googleBtnRef.current) return;
    if (step.kind !== "form") return;

    window.google?.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
    });
    window.google?.accounts.id.renderButton(googleBtnRef.current, {
      theme: "outline",
      size: "large",
      width: googleBtnRef.current.offsetWidth,
      text: "signin_with",
      locale: "it",
    });
  }, [googleReady, step.kind]);

  async function handleGoogleResponse(response: { credential: string }) {
    setLoading(true);
    setErrorMsg("");
    try {
      await googleLogin(response.credential);
      navigate(returnTo || "/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        try {
          const payload = JSON.parse(atob(response.credential.split(".")[1]));
          setStep({ kind: "not_found", email: payload.email || "" });
        } catch {
          setStep({ kind: "not_found", email: "" });
        }
      } else {
        setErrorMsg("Errore durante l'accesso con Google. Riprova.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleLoginSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!identifier.trim() || !password.trim()) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const u = await espoLogin(identifier, password);
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else if (u.roles.includes("operatore") && !u.roles.includes("affiliato")) {
        navigate(paginaArrivoOperatore(u.vociMenu), { replace: true });
      } else if (u.roles.includes("affiliato")) {
        navigate("/affiliato/dashboard", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      console.error("Espo login failed:", err);
      setErrorMsg("Credenziali non valide. Riprova.");
    } finally {
      setLoading(false);
    }
  }

  // Percorso magic-link secondario: stessa logica di probe usata prima per
  // decidere password vs link, ora innescata esplicitamente dal link
  // "Accedi con link via email" invece che automaticamente al submit.
  async function handleMagicLinkClick() {
    if (!identifier.trim()) {
      setErrorMsg("Inserisci email o username per ricevere il link.");
      return;
    }
    if (!identifier.includes("@")) {
      setErrorMsg("Il login con link richiede un indirizzo email.");
      return;
    }
    setLoading(true);
    setErrorMsg("");
    try {
      const mode = await probe(identifier);
      if (mode === "password") {
        setErrorMsg("Questo account usa la password: inseriscila qui sopra.");
      } else {
        setStep({ kind: "magic_sent", identifier });
      }
    } catch (err) {
      console.error("Probe failed:", err);
      setErrorMsg("Si è verificato un errore. Riprova tra qualche istante.");
    } finally {
      setLoading(false);
    }
  }

  function resetToForm() {
    setStep({ kind: "form" });
    setPassword("");
    setErrorMsg("");
  }

  async function handleForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!forgotIdentifier.trim()) return;
    setLoading(true);
    setErrorMsg("");
    try {
      await requestPasswordReset(forgotIdentifier.trim());
      setStep({ kind: "forgot_sent" });
    } catch {
      setErrorMsg("Si è verificato un errore. Riprova.");
    } finally {
      setLoading(false);
    }
  }

  // === Render: step=not_found ===
  if (step.kind === "not_found") {
    return (
      <AuthCard>
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <div className="inline-flex bg-brand/10 rounded-2xl p-3 mb-4">
            <Search size={26} className="text-brand" />
          </div>
          <h2 className="text-xl font-semibold text-text mb-2">Account non trovato</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {step.email ? (
              <>L'email <strong>{step.email}</strong> non è ancora registrata.</>
            ) : (
              <>L'email non è ancora registrata.</>
            )}
            {" "}Puoi effettuare il tuo cambio in autonomia dal nostro portale, parlando con il nostro esperto AI.
          </p>
          <a
            href="https://www.aienergia.it"
            className="inline-block w-full bg-brand text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors text-center"
          >
            Inizia su aienergia.it
          </a>
          <button
            type="button"
            onClick={resetToForm}
            className="w-full text-center text-xs text-brand underline mt-4"
          >
            Cambia utente
          </button>
        </div>
      </AuthCard>
    );
  }

  // === Render: step=forgot_password ===
  if (step.kind === "forgot_password") {
    return (
      <AuthCard>
        <div className="bg-card rounded-xl border border-border p-8">
          <h2 className="text-lg font-semibold text-text mb-2">Password dimenticata</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Inserisci la tua email o username per ricevere un link di reset.
          </p>
          <form onSubmit={handleForgotSubmit} className="space-y-3">
            <div>
              <label htmlFor="forgot-identifier" className="block text-xs font-medium text-text mb-1">Email o username</label>
              <input
                id="forgot-identifier"
                type="text"
                value={forgotIdentifier}
                onChange={(e) => setForgotIdentifier(e.target.value)}
                autoFocus
                disabled={loading}
                className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-brand disabled:opacity-50"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !forgotIdentifier.trim()}
              className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50"
            >
              {loading ? "Invio..." : "Invia link di reset"}
            </button>
            {errorMsg && (
              <p className="text-xs text-red-600 text-center">{errorMsg}</p>
            )}
          </form>
          <button
            type="button"
            onClick={resetToForm}
            className="w-full text-center text-xs text-brand underline mt-4"
          >
            Torna al login
          </button>
        </div>
      </AuthCard>
    );
  }

  // === Render: step=forgot_sent ===
  if (step.kind === "forgot_sent") {
    return (
      <AuthCard>
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <div className="inline-flex bg-brand/10 rounded-2xl p-3 mb-4">
            <Mail size={26} className="text-brand" />
          </div>
          <h2 className="text-xl font-semibold text-text mb-2">Controlla la tua email</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Se l'account esiste, riceverai un'email con le istruzioni per reimpostare la password.
          </p>
          <button
            type="button"
            onClick={resetToForm}
            className="text-sm text-brand underline"
          >
            Torna al login
          </button>
        </div>
      </AuthCard>
    );
  }

  // === Render: step=magic_sent ===
  if (step.kind === "magic_sent") {
    return (
      <AuthCard>
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          {expired && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-lg mb-4">
              La sessione è scaduta. Effettua di nuovo il login.
            </div>
          )}
          <div className="inline-flex bg-brand/10 rounded-2xl p-3 mb-4">
            <Mail size={26} className="text-brand" />
          </div>
          <h2 className="text-xl font-semibold text-text mb-2">Controlla la tua email</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Se l'email <strong>{step.identifier}</strong> è registrata, riceverai un link per accedere.
            Clicca sul link nella mail per entrare nel portale.
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            Se non lo trovi, controlla la cartella spam.
          </p>
          <button
            type="button"
            onClick={resetToForm}
            className="text-sm text-brand underline"
          >
            Cambia utente
          </button>
        </div>
      </AuthCard>
    );
  }

  // === Render: step=form — schermata unica username+password, split animato ===
  return (
    <div className="login relative z-10 flex flex-col md:flex-row w-full max-w-[940px] min-h-[560px] mx-auto rounded-2xl overflow-hidden shadow-[0_50px_120px_-30px_rgba(0,0,0,0.6)]">
      {/* Pannello art animato (aurora + rings + sparks, valori-prop senza numeri) */}
      <div className="login-art relative flex-1 min-h-[220px] overflow-hidden p-8 md:p-10 flex flex-col text-[#EAF3E6]">
        <div className="glow" />
        <div className="ring" style={{ width: 260, height: 260, top: -80, right: -70 }} />
        <div className="ring" style={{ width: 160, height: 160, bottom: 40, left: -50, animationDelay: "2s" }} />
        <div className="ring" style={{ width: 90, height: 90, top: 120, right: 60, animationDelay: "4s" }} />
        <span className="spark" style={{ left: "18%", animationDuration: "6s", animationDelay: "0s" }} />
        <span className="spark" style={{ left: "40%", animationDuration: "8s", animationDelay: "1.4s" }} />
        <span className="spark" style={{ left: "63%", animationDuration: "7s", animationDelay: "0.6s" }} />
        <span className="spark" style={{ left: "82%", animationDuration: "9s", animationDelay: "2.2s" }} />
        <span className="spark" style={{ left: "52%", animationDuration: "6.5s", animationDelay: "3.1s" }} />

        <img src="/logo.png" alt="AIEnergia" className="relative h-7 w-auto self-start brightness-0 invert mb-auto" />

        <h2 className="relative font-heading text-3xl font-bold tracking-tight leading-[1.15] mt-auto">
          L'energia giusta,<br />scelta con l'AI.
        </h2>
        <p className="relative text-[#a9d69a] mt-3 text-sm max-w-[300px] leading-relaxed">
          Carica la bolletta, l'assistente trova l'offerta migliore e attiva il contratto per te.
        </p>
        <div className="relative flex gap-2.5 mt-6 flex-wrap">
          <span className="flex gap-1.5 items-center bg-white/[0.09] border border-[#8de05a]/25 rounded-full px-3.5 py-1.5 text-xs text-[#dbeed3]">
            <ShieldCheck size={14} className="text-[#8de05a]" /> Tutti i principali fornitori
          </span>
          <span className="flex gap-1.5 items-center bg-white/[0.09] border border-[#8de05a]/25 rounded-full px-3.5 py-1.5 text-xs text-[#dbeed3]">
            <Sparkles size={14} className="text-[#8de05a]" /> Attivazione guidata dall'AI
          </span>
        </div>
      </div>

      {/* Pannello form */}
      <div className="w-full md:w-[380px] bg-card p-8 md:p-11 flex flex-col justify-center">
        <img src="/logo.png" alt="AIEnergia" className="h-7 w-auto self-start mb-6" />
        <div className="font-heading font-bold text-xl text-text mb-1">Bentornato</div>
        <p className="text-sm text-muted-foreground mb-5">Accedi al tuo portale</p>

        {inAttesa > 0 && (
          <div className="bg-card border border-border text-text text-sm px-4 py-2 rounded-lg mb-4">
            {dopoAccesso(inAttesa)}
          </div>
        )}

        {expired && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-lg mb-4">
            La sessione è scaduta. Effettua di nuovo il login.
          </div>
        )}

        <form onSubmit={handleLoginSubmit} className="space-y-3">
          <div>
            <label htmlFor="login-identifier" className="block text-xs font-medium text-text mb-1">Email o username</label>
            <input
              id="login-identifier"
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              autoFocus
              placeholder="tua@email.it oppure username"
              disabled={loading}
              className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-brand disabled:opacity-50"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-xs font-medium text-text mb-1">Password</label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={loading}
                className="w-full px-3 py-2.5 pr-10 border border-border rounded-lg text-sm outline-none focus:border-brand disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Nascondi i caratteri" : "Mostra i caratteri"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-text"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="text-right -mt-1">
            <button
              type="button"
              onClick={() => {
                setStep({ kind: "forgot_password" });
                setForgotIdentifier(identifier);
                setErrorMsg("");
              }}
              className="text-xs text-brand font-medium hover:underline"
            >
              Password dimenticata?
            </button>
          </div>
          <button
            type="submit"
            disabled={loading || !identifier.trim() || !password.trim()}
            className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50"
          >
            {loading ? "Accesso..." : "Accedi"}
          </button>
          {errorMsg && (
            <p className="text-xs text-red-600 text-center">{errorMsg}</p>
          )}
        </form>

        <button
          type="button"
          onClick={handleMagicLinkClick}
          disabled={loading}
          className="w-full text-center text-xs text-muted-foreground hover:text-brand transition-colors mt-4 pt-4 border-t border-border disabled:opacity-50"
        >
          Accedi con link via email
        </button>

        {GOOGLE_CLIENT_ID && (
          <>
            <div className="flex items-center gap-3 mt-4 mb-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">oppure</span>
              <div className="flex-1 h-px bg-border" />
            </div>
            <div ref={googleBtnRef} className="w-full" />
          </>
        )}
      </div>
    </div>
  );
}
